import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardText, Export } from '@phosphor-icons/react';

const ACT_COLOR = {
  lead_created: '#2B6EF0', stage_changed: '#8B5CF6', payment_recorded: '#10A882',
  user_created: '#E5A100', branch_updated: '#06B6D4', invoice_created: '#F97316',
  branch_created: '#06B6D4', branch_deleted: '#EF4444', user_updated: '#E5A100',
  course_created: '#8B5CF6', course_updated: '#8B5CF6', automation_created: '#F97316',
  automation_toggled: '#F97316', automation_deleted: '#EF4444', bulk_stage_change: '#6366F1',
  pipeline_stages_updated: '#2B6EF0', payment_reminder_sent: '#10A882', upi_payment_confirmed: '#10A882',
};

const ACTION_TYPES = [
  'lead_created', 'stage_changed', 'bulk_stage_change',
  'payment_recorded', 'payment_reminder_sent', 'upi_payment_confirmed',
  'user_created', 'user_updated',
  'branch_created', 'branch_updated', 'branch_deleted',
  'course_created', 'course_updated',
  'automation_created', 'automation_toggled', 'automation_deleted',
  'pipeline_stages_updated',
];

const ENTITY_TYPES = ['lead', 'user', 'branch', 'course', 'automation', 'payment', 'invoice', 'settings'];

export default function AuditLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    api.get('/api/users').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter !== 'all') params.action = actionFilter;
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (userFilter !== 'all') params.user_id = userFilter;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate + 'T23:59:59').toISOString();
      const { data } = await api.get('/api/audit-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [actionFilter, entityFilter, userFilter, startDate, endDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleExportCSV = () => {
    if (logs.length === 0) return;
    const headers = ['Action', 'Entity Type', 'Entity ID', 'Details', 'User', 'Date'];
    const rows = logs.map(log => [
      log.action || '',
      log.entity_type || '',
      log.entity_id || '',
      `"${(log.details || '').replace(/"/g, '""')}"`,
      log.user_name || '',
      log.created_at || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'audit_logs.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Sidebar already hides this link for anyone but super_admin/branch_manager --
  // defense-in-depth check for anyone navigating here directly by URL.
  if (!['super_admin', 'branch_manager'].includes(user?.role)) {
    return (
      <div data-testid="audit-logs-access-denied" className="text-sm text-muted-foreground text-center py-24">
        You don't have access to this page.
      </div>
    );
  }

  return (
    <div data-testid="audit-logs-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Audit Logs</h1>
          <p className="text-sm text-muted-foreground mt-1"><span className="font-mono">{total}</span> logged actions</p>
        </div>
        <Button data-testid="export-audit-csv" variant="outline" className="h-9 rounded-lg text-xs gap-1.5 font-semibold" onClick={handleExportCSV}>
          <Export size={14} weight="bold" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="audit-filters">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger data-testid="audit-action-filter" className="w-44 h-9 text-xs rounded-lg">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger data-testid="audit-entity-filter" className="w-36 h-9 text-xs rounded-lg">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger data-testid="audit-user-filter" className="w-40 h-9 text-xs rounded-lg">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="date"
          data-testid="audit-start-date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="w-36 h-9 text-xs rounded-lg"
          placeholder="Start Date"
        />
        <Input
          type="date"
          data-testid="audit-end-date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          className="w-36 h-9 text-xs rounded-lg"
          placeholder="End Date"
        />
      </div>

      <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid="audit-table">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Action</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Entity</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Details</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">User</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Date</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground h-32 font-mono">loading...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center h-32">
                <div className="flex flex-col items-center gap-2 text-muted-foreground"><ClipboardText size={24} /><span className="text-xs font-mono">no logs yet</span></div>
              </TableCell></TableRow>
            ) : logs.map((log, i) => (
              <TableRow key={i} data-testid={`audit-row-${i}`}>
                <TableCell className="py-2.5"><span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ACT_COLOR[log.action] || '#808080' }}>{log.action?.replace(/_/g, ' ')}</span></TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 capitalize">{log.entity_type}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 max-w-xs truncate">{log.details}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5">{log.user_name}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
